import { View, StyleSheet } from "react-native";
import {
  useGetUserLearningPreferencesQuery,
  useUpdateUserLearningPreferencesMutation,
} from "../../../../graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import AccordionShell from "../../../../components/common/general/CustomAccordion/AccordionShell";
import SettingsAccordionItem from "../../../../components/common/general/CustomAccordion/SettingsAccordionItem";
import { Sparkles, BookOpen, GraduationCap, Target, Bell } from "lucide-react";
import { useCompanionPreferencesForm } from "../../../../hooks/useCompanionPreferencesForm";
import { useEffect, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import InterestsEditor from "../../../../components/companion/preferences/editors/InterestsEditor";
import CareerInterestsEditor from "../../../../components/companion/preferences/editors/CareerInterestsEditor";
import LearningPreferencesEditor from "../../../../components/companion/preferences/editors/LearningPreferencesEditor";
import NotificationRedirectPanel from "../../../../components/companion/preferences/editors/NotificationRedirectPanel";
import { useWatch } from "react-hook-form";
import MotivationEditor from "../../../../components/companion/preferences/editors/MotivationsEditor";
import AsyncStorage from "@react-native-async-storage/async-storage";

const FIRST_OPEN_KEY = "ai_companion_settings_first_open_v1";

type Section = "interests" | "academic" | "learning" | "motivation" | "reminders";

export const AICompanionSettings = () => {
  const [openSections, setOpenSections] = useState<Set<Section>>(() => new Set(["interests"]));
  const lastSentRef = useRef<string | null>(null);
  const isHydratingRef = useRef(true);

  const { data, loading } = useGetUserLearningPreferencesQuery();
  const [updatePreferences] = useUpdateUserLearningPreferencesMutation();

  const methods = useCompanionPreferencesForm();
  const { watch, setValue, formState } = methods;

  const prefs = data?.getUserLearningPreferences;

  const skillsToImprove = useWatch({ control: methods.control, name: "skillsToImprove" });
  const learningStyles = useWatch({ control: methods.control, name: "learningStyles" });
  const motivations = useWatch({ control: methods.control, name: "motivations" });
  const semesterGoal = useWatch({ control: methods.control, name: "semesterGoal" });

  const values = useWatch({ control: methods.control });

  const formValues = useWatch({ control: methods.control }) ?? {};

  useEffect(() => {
    if (loading) return;

    isHydratingRef.current = true;

    methods.reset({
      interests: prefs?.interests ?? [],
      careerInterests: prefs?.careerInterests ?? [],
      skillsToImprove: prefs?.skillsToImprove ?? [],
      learningStyles: prefs?.learningStyles ?? [],
      motivations: prefs?.motivations ?? [],
      semesterGoal: prefs?.semesterGoal ?? "",
      assignmentReminders: prefs?.assignmentReminders ?? true,
      studyTimePreference: prefs?.studyTimePreference ?? null,
      checkInFrequency: prefs?.checkInFrequency ?? null,
      textReminders: prefs?.textReminders ?? false,
    });

    // allow next tick to finish RHF internal state update
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [prefs, loading, methods]);

  useEffect(() => {
    (async () => {
      const hasOpened = await AsyncStorage.getItem(FIRST_OPEN_KEY);
      if (!hasOpened) {
        setOpenSections(new Set(["interests"]));
        await AsyncStorage.setItem(FIRST_OPEN_KEY, "1");
      }
    })().catch(console.error);
  }, []);
  useEffect(() => {
    if (isHydratingRef.current) return;
    if (!values) return;

    const serialized = JSON.stringify(values);
    if (serialized === lastSentRef.current) return;

    lastSentRef.current = serialized;

    updatePreferences({
      variables: { input: values },
    }).catch(console.error);
  }, [values, formState.isDirty, updatePreferences]);

  const toggleSection = (section: Section) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  if (loading) {
    return <Skeleton height={200} />;
  }

  return (
    <View style={styles.container}>
      <FormProvider {...methods}>
        <AccordionShell>
          <SettingsAccordionItem
            index={0}
            icon={<Sparkles size={18} />}
            title="Personal Interests"
            rightLabel={`${formValues?.interests?.length ?? 0} selected`}
            expanded={openSections.has("interests")}
            onToggle={() => toggleSection("interests")}>
            <InterestsEditor
              value={watch("interests")}
              onChange={(v) => setValue("interests", v, { shouldDirty: true })}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={1}
            icon={<BookOpen size={18} />}
            title="Academic Interests"
            rightLabel={`${formValues?.careerInterests?.length ?? 0} selected`}
            expanded={openSections.has("academic")}
            onToggle={() => toggleSection("academic")}>
            <CareerInterestsEditor
              value={watch("careerInterests")}
              onChange={(v) => setValue("careerInterests", v, { shouldDirty: true })}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={2}
            icon={<GraduationCap size={18} />}
            title="How You Learn"
            rightLabel={`${(skillsToImprove?.length ?? 0) + (learningStyles?.length ?? 0)} selected`}
            expanded={openSections.has("learning")}
            onToggle={() => toggleSection("learning")}>
            <LearningPreferencesEditor
              value={{
                skillsToImprove: skillsToImprove ?? [],
                learningStyles: learningStyles ?? [],
              }}
              onChange={(v) => {
                setValue("skillsToImprove", v.skillsToImprove, { shouldDirty: true });
                setValue("learningStyles", v.learningStyles, { shouldDirty: true });
              }}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={3}
            icon={<Target size={18} />}
            title="What Drives You"
            rightLabel={`${motivations?.length ?? 0} selected`}
            expanded={openSections.has("motivation")}
            onToggle={() => toggleSection("motivation")}>
            <MotivationEditor
              value={{
                motivations: motivations ?? [],
                semesterGoal: semesterGoal ?? "",
              }}
              onChange={(v) => {
                setValue("motivations", v.motivations, { shouldDirty: true });
                setValue("semesterGoal", v.semesterGoal, { shouldDirty: true });
              }}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={4}
            icon={<Bell size={18} />}
            title="Stay on Track"
            rightLabel={formValues?.assignmentReminders ? "On" : "Off"}
            expanded={openSections.has("reminders")}
            onToggle={() => toggleSection("reminders")}
            isLast>
            <NotificationRedirectPanel />
          </SettingsAccordionItem>
        </AccordionShell>
      </FormProvider>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    gap: 24,
  },
  content: {
    gap: 16,
    paddingTop: 8,
  },
  row: {
    gap: 4,
  },
});
